import { createIcon } from '../../common/lucide-icons/lucide-icons.js';
import { disposeVideo, startVideoFromInteraction } from '../video-sources/video-sources.js';

let photoSwipeViewerPromise = null;
let videoOpening = false;

function loadPhotoSwipeViewer() {
    if (!photoSwipeViewerPromise) {
        photoSwipeViewerPromise =
            import('../../common/photoswipe-viewer/photoswipe-viewer.js').catch((error) => {
                photoSwipeViewerPromise = null;
                throw error;
            });
    }
    return photoSwipeViewerPromise;
}

function formatTime(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const minutes = Math.floor(seconds / 60);
    return `${minutes}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
}

function setButtonIcon(button, name, label) {
    button.setAttribute('aria-label', label);
    button.replaceChildren(createIcon(name));
}

function initVideoLibrary(root) {
    if (root.dataset.mediaLibraryReady === 'true') return;
    root.dataset.mediaLibraryReady = 'true';
    const links = [...root.querySelectorAll('[data-product-video]')];
    links.forEach((link, index) => {
        link.addEventListener('click', async (event) => {
            if (
                event.defaultPrevented ||
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
            )
                return;
            event.preventDefault();
            if (videoOpening || root.ownerDocument.querySelector('.pswp')) return;
            videoOpening = true;
            // Keep this call before the first await: Safari authorizes the media
            // element, not a later replacement created after the viewer import.
            const initialVideo = startVideoFromInteraction(
                link.querySelector('.z-product-media__video-poster') || link,
                {
                    mp4: link.href,
                    webm: link.dataset.videoWebm || '',
                    mp4_type: link.dataset.videoType || 'video/mp4',
                },
                {
                    poster: link.dataset.videoPoster || '',
                    label: link.dataset.videoTitle,
                    className: 'z-product-media__video-preview',
                },
            );
            let transferred = false;
            let cancelled = false;
            const cancelPending = () => {
                cancelled = true;
                disposeVideo(initialVideo);
            };
            const view = root.ownerDocument.defaultView;
            view.addEventListener('pagehide', cancelPending, { once: true });
            try {
                const { openPhotoSwipeViewer } = await loadPhotoSwipeViewer();
                if (!root.isConnected || cancelled) return;
                const viewer = openPhotoSwipeViewer({
                    productMedia: true,
                    initialVideo,
                    slides: links.map((item) => ({
                        type: 'video',
                        videoSrc: item.href,
                        videoWebm: item.dataset.videoWebm || '',
                        videoType: item.dataset.videoType || 'video/mp4',
                        poster: item.dataset.videoPoster || '',
                        posterSrcset: item.dataset.videoPosterSrcset || '',
                        width: Number(item.dataset.videoWidth) || 1500,
                        height: Number(item.dataset.videoHeight) || 1500,
                        alt: item.dataset.videoTitle || 'Produktvideo',
                    })),
                    index,
                });
                transferred = Boolean(viewer);
            } catch {
                window.location.assign(link.href);
            } finally {
                view.removeEventListener('pagehide', cancelPending);
                if (!transferred) disposeVideo(initialVideo);
                videoOpening = false;
            }
        });
    });
}

function initAudioLibrary(root) {
    if (root.dataset.mediaLibraryReady === 'true') return;
    root.dataset.mediaLibraryReady = 'true';
    const audio = root.querySelector('[data-product-audio-element]');
    const controls = root.querySelector('[data-product-audio-controls]');
    const toggle = root.querySelector('[data-product-audio-toggle]');
    const progress = root.querySelector('[data-product-audio-progress]');
    const time = root.querySelector('[data-product-audio-time]');
    const title = root.querySelector('[data-product-audio-title]');
    const cover = root.querySelector('[data-product-audio-cover]');
    const status = root.querySelector('[data-product-audio-status]');
    const tracks = [...root.querySelectorAll('[data-product-audio-track]')];
    let activeIndex = 0;

    if (!audio || !controls || !toggle || !progress || !time || !title || !tracks.length) return;

    root.classList.add('is-enhanced');
    controls.hidden = false;

    const updatePlaybackState = () => {
        setButtonIcon(
            toggle,
            audio.paused ? 'play' : 'pause',
            audio.paused ? 'Audio abspielen' : 'Audio pausieren',
        );
        root.classList.toggle('is-playing', !audio.paused);
    };

    const updateProgress = () => {
        progress.value = audio.duration ? String((audio.currentTime / audio.duration) * 1000) : '0';
        time.textContent = `${formatTime(audio.currentTime)} / ${formatTime(audio.duration)}`;
    };

    const updateCover = (source, srcset, scale, alt, fit, position) => {
        if (!cover) return;
        cover.style.setProperty('--z-audio-cover-width', scale ? '138%' : '100%');
        cover.style.setProperty('--z-audio-cover-fit', fit || 'cover');
        cover.style.setProperty('--z-audio-cover-position', position || '50% 50%');
        if (!source) {
            cover.replaceChildren(createIcon('audio-lines'));
            return;
        }
        let image = cover.querySelector('img');
        if (!image) {
            image = document.createElement('img');
            image.width = 112;
            image.height = 112;
            image.alt = '';
            cover.replaceChildren(image);
        }
        image.sizes = scale ? '200px' : '(max-width: 47.999rem) 92px, 112px';
        if (srcset) image.srcset = srcset;
        else image.removeAttribute('srcset');
        image.src = source;
        image.alt = alt ? `Cover zu ${alt}` : '';
    };

    const selectTrack = (index, autoplay = true) => {
        activeIndex = Math.max(0, Math.min(tracks.length - 1, index));
        const track = tracks[activeIndex];
        const trackTitle = track.dataset.audioTitle || `Audio ${activeIndex + 1}`;

        if (audio.src !== track.href) {
            audio.src = track.href;
            audio.load();
        }
        title.textContent = trackTitle;
        updateCover(
            track.dataset.audioImage,
            track.dataset.audioSrcset,
            track.dataset.audioScale,
            trackTitle,
            track.dataset.audioFit,
            track.dataset.audioPosition,
        );
        tracks.forEach((item, itemIndex) => {
            const active = itemIndex === activeIndex;
            item.closest('.z-product-media__audio-card')?.classList.toggle('is-active', active);
            if (active) item.setAttribute('aria-current', 'true');
            else item.removeAttribute('aria-current');
        });
        status.textContent = `${trackTitle} ausgewählt.`;
        updateProgress();

        if (autoplay) {
            const playback = audio.play();
            if (playback?.catch) {
                playback.catch(() => {
                    status.textContent =
                        'Die Audiodatei konnte nicht automatisch gestartet werden.';
                    updatePlaybackState();
                });
            }
        }
    };

    tracks.forEach((track, index) => {
        track.addEventListener('click', (event) => {
            event.preventDefault();
            if (index === activeIndex && !audio.paused) audio.pause();
            else if (index === activeIndex) {
                const playback = audio.play();
                if (playback?.catch) playback.catch(updatePlaybackState);
            } else selectTrack(index);
        });
    });

    toggle.addEventListener('click', () => {
        if (audio.paused) {
            const playback = audio.play();
            if (playback?.catch) playback.catch(updatePlaybackState);
        } else audio.pause();
    });
    progress.addEventListener('input', () => {
        if (audio.duration) audio.currentTime = audio.duration * (Number(progress.value) / 1000);
    });
    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('loadedmetadata', updateProgress);
    audio.addEventListener('durationchange', updateProgress);
    audio.addEventListener('play', updatePlaybackState);
    audio.addEventListener('pause', updatePlaybackState);
    audio.addEventListener('ended', () => {
        if (activeIndex < tracks.length - 1) selectTrack(activeIndex + 1);
        else updatePlaybackState();
    });
    audio.addEventListener('error', () => {
        status.textContent = 'Die Audiodatei konnte nicht geladen werden.';
        updatePlaybackState();
    });
    updatePlaybackState();
    updateProgress();
}

export function initMediaLibraries(root = document) {
    for (const [selector, initialize] of [
        ['[data-video-library]', initVideoLibrary],
        ['[data-product-audio]', initAudioLibrary],
    ]) {
        if (root.matches?.(selector)) initialize(root);
        root.querySelectorAll(selector).forEach(initialize);
    }
}
